import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, Tag, Button, Space, Row, Col, Descriptions, message,
    Popconfirm, Typography, Modal, Form, Input, Empty, Spin
} from 'antd';
import {
    ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined,
    ClockCircleOutlined, FlagOutlined, DeleteOutlined, UserOutlined,
    PhoneOutlined, MailOutlined, EnvironmentOutlined, CalendarOutlined
} from '@ant-design/icons';
import ambassadorsService from '../../common/services/ambassadors.service';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const DetailApplicationPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [application, setApplication] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Reject modal
    const [rejectModalVisible, setRejectModalVisible] = useState(false);
    const [rejectForm] = Form.useForm();
    const [rejecting, setRejecting] = useState(false);

    // Fetch application data
    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await ambassadorsService.getApplicationById(id);
            if (response.success) {
                setApplication(response.data);
            } else {
                message.error('Candidature non trouvee');
                navigate('/dashboard/ambassador-applications');
            }
        } catch (error) {
            console.error('Erreur:', error);
            message.error('Erreur lors du chargement');
            navigate('/dashboard/ambassador-applications');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    // Helper functions
    const getProfileTypeLabel = (type) => {
        const labels = {
            'user_client': 'Utilisateur/Client',
            'professional': 'Professionnel',
            'driver_operator': 'Chauffeur/Operateur',
            'influencer': 'Influenceur',
            'entrepreneur_leader': 'Entrepreneur/Leader',
            'other': 'Autre'
        };
        return labels[type] || type;
    };

    const getPromotionMethodLabel = (method) => {
        const labels = {
            'word_of_mouth': 'Bouche-a-oreille',
            'social_media': 'Reseaux sociaux',
            'field_actions': 'Terrain/Actions locales',
            'professional_recommendations': 'Recommandations pro',
            'other': 'Autre'
        };
        return labels[method] || method;
    };

    const getAvailabilityLabel = (value) => {
        const labels = {
            'occasional': 'Occasionnel',
            'regular': 'Regulier',
            'very_active': 'Tres actif'
        };
        return labels[value] || value;
    };

    const getCommunitySizeLabel = (value) => {
        const labels = {
            'moins_100': '< 100 personnes',
            '100_500': '100-500 personnes',
            '500_1000': '500-1000 personnes',
            '1000_5000': '1000-5000 personnes',
            'plus_5000': '> 5000 personnes'
        };
        return labels[value] || value;
    };

    // Actions
    const handleApprove = async () => {
        setActionLoading(true);
        try {
            const response = await ambassadorsService.approveApplication(id);
            if (response.success) {
                message.success('Candidature approuvee ! Un compte ambassadeur a ete cree.');
                fetchData();
            } else {
                message.error(response.message || 'Erreur lors de l\'approbation');
            }
        } catch (error) {
            console.error('Erreur:', error);
            message.error('Erreur lors de l\'approbation');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async (values) => {
        setRejecting(true);
        try {
            const response = await ambassadorsService.rejectApplication(id, values.reason);
            if (response.success) {
                message.success('Candidature rejetee');
                setRejectModalVisible(false);
                rejectForm.resetFields();
                fetchData();
            } else {
                message.error(response.message || 'Erreur lors du rejet');
            }
        } catch (error) {
            console.error('Erreur:', error);
            message.error('Erreur lors du rejet');
        } finally {
            setRejecting(false);
        }
    };

    const handleDelete = async () => {
        try {
            const response = await ambassadorsService.deleteApplication(id);
            if (response.success) {
                message.success('Candidature supprimee definitivement');
                navigate('/dashboard/ambassador-applications');
            } else {
                message.error(response.message || 'Erreur lors de la suppression');
            }
        } catch (error) {
            console.error('Erreur:', error);
            message.error('Erreur lors de la suppression');
        }
    };

    if (loading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!application) {
        return (
            <div style={{ padding: '24px' }}>
                <Empty description="Candidature non trouvee" />
            </div>
        );
    }

    const statusConfig = {
        pending: { color: 'warning', icon: <ClockCircleOutlined />, text: 'En attente' },
        approved: { color: 'success', icon: <CheckCircleOutlined />, text: 'Approuvee' },
        rejected: { color: 'error', icon: <CloseCircleOutlined />, text: 'Rejetee' }
    };
    const currentStatus = statusConfig[application.status] || statusConfig.pending;

    return (
        <div style={{ padding: '24px' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Space>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/dashboard/ambassador-applications')}
                    >
                        Retour
                    </Button>
                    <Title level={3} style={{ margin: 0 }}>
                        <FlagOutlined style={{ marginRight: '12px' }} />
                        Detail de la Candidature
                    </Title>
                </Space>
                <Space>
                    {application.status === 'pending' && (
                        <>
                            <Button
                                danger
                                onClick={() => setRejectModalVisible(true)}
                                loading={actionLoading}
                            >
                                Rejeter
                            </Button>
                            <Popconfirm
                                title="Approuver cette candidature ?"
                                description="Un compte ambassadeur sera cree automatiquement."
                                onConfirm={handleApprove}
                                okText="Oui, approuver"
                                cancelText="Annuler"
                            >
                                <Button type="primary" loading={actionLoading}>
                                    Approuver
                                </Button>
                            </Popconfirm>
                        </>
                    )}
                    <Popconfirm
                        title="Supprimer cette candidature ?"
                        description="Cette action est irreversible."
                        onConfirm={handleDelete}
                        okText="Supprimer"
                        cancelText="Annuler"
                        okButtonProps={{ danger: true }}
                    >
                        <Button danger icon={<DeleteOutlined />}>
                            Supprimer
                        </Button>
                    </Popconfirm>
                </Space>
            </div>

            <Row gutter={[24, 24]}>
                {/* Left Column - Informations personnelles */}
                <Col xs={24} lg={8}>
                    <Card
                        title={
                            <Space>
                                <UserOutlined style={{ color: '#D4A853' }} />
                                <span>Informations personnelles</span>
                            </Space>
                        }
                    >
                        <div style={{ textAlign: 'center', marginBottom: 24 }}>
                            <div style={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #D4A853 0%, #c49a4b 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px',
                                fontSize: 32,
                                color: '#fff'
                            }}>
                                {application.firstName?.charAt(0)}{application.lastName?.charAt(0)}
                            </div>
                            <Title level={4} style={{ margin: 0 }}>
                                {application.firstName} {application.lastName}
                            </Title>
                            <Tag color={currentStatus.color} icon={currentStatus.icon} style={{ marginTop: 8 }}>
                                {currentStatus.text}
                            </Tag>
                        </div>

                        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                <PhoneOutlined style={{ color: '#D4A853', fontSize: 18 }} />
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Telephone</Text>
                                    <div style={{ fontWeight: 500 }}>{application.phone}</div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                <MailOutlined style={{ color: '#D4A853', fontSize: 18 }} />
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Email</Text>
                                    <div style={{ fontWeight: 500 }}>{application.email || '-'}</div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                <EnvironmentOutlined style={{ color: '#D4A853', fontSize: 18 }} />
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Ville</Text>
                                    <div style={{ fontWeight: 500 }}>{application.city || '-'}</div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <CalendarOutlined style={{ color: '#D4A853', fontSize: 18 }} />
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Date de candidature</Text>
                                    <div style={{ fontWeight: 500 }}>
                                        {dayjs(application.createdAt).format('DD/MM/YYYY HH:mm')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </Col>

                {/* Right Column - Details de la candidature */}
                <Col xs={24} lg={16}>
                    <Card title="Profil et Motivation" style={{ marginBottom: 24 }}>
                        <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
                            <Descriptions.Item label="Type de profil" span={2}>
                                {application.profileType?.length > 0
                                    ? application.profileType.map(p => getProfileTypeLabel(p)).join(', ')
                                    : '-'}
                                {application.profileTypeOther && ` (${application.profileTypeOther})`}
                            </Descriptions.Item>
                            <Descriptions.Item label="A une communaute">
                                <Tag color={application.hasCommunity ? 'green' : 'default'}>
                                    {application.hasCommunity ? 'Oui' : 'Non'}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Taille communaute">
                                {application.hasCommunity && application.communitySize
                                    ? getCommunitySizeLabel(application.communitySize)
                                    : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Reseaux sociaux" span={2}>
                                {application.socialNetworks || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Liens reseaux" span={2}>
                                {application.socialLinks || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Motivation" span={2}>
                                <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                    {application.motivation || '-'}
                                </Paragraph>
                            </Descriptions.Item>
                            <Descriptions.Item label="Methodes de promotion" span={2}>
                                {application.promotionMethods?.length > 0
                                    ? application.promotionMethods.map(m => getPromotionMethodLabel(m)).join(', ')
                                    : '-'}
                                {application.promotionMethodOther && ` (${application.promotionMethodOther})`}
                            </Descriptions.Item>
                            <Descriptions.Item label="Disponibilite" span={2}>
                                <Tag color={application.availability === 'very_active' ? 'green' : application.availability === 'regular' ? 'blue' : 'default'}>
                                    {getAvailabilityLabel(application.availability)}
                                </Tag>
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    <Card title="Engagements et Statut">
                        <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
                            <Descriptions.Item label="Accepte representation">
                                <Tag color={application.acceptsRepresentation ? 'green' : 'red'}>
                                    {application.acceptsRepresentation ? 'Oui' : 'Non'}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Accepte conditions">
                                <Tag color={application.acceptsTerms ? 'green' : 'red'}>
                                    {application.acceptsTerms ? 'Oui' : 'Non'}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Statut actuel" span={2}>
                                <Tag color={currentStatus.color} icon={currentStatus.icon}>
                                    {currentStatus.text}
                                </Tag>
                            </Descriptions.Item>
                            {application.status === 'rejected' && application.rejectionReason && (
                                <Descriptions.Item label="Raison du rejet" span={2}>
                                    <Text type="danger">{application.rejectionReason}</Text>
                                </Descriptions.Item>
                            )}
                            {application.processedAt && (
                                <Descriptions.Item label="Traitee le" span={2}>
                                    {dayjs(application.processedAt).format('DD/MM/YYYY HH:mm')}
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </Card>
                </Col>
            </Row>

            {/* Reject Modal */}
            <Modal
                title="Rejeter la candidature"
                open={rejectModalVisible}
                onCancel={() => {
                    setRejectModalVisible(false);
                    rejectForm.resetFields();
                }}
                footer={null}
            >
                <Form
                    form={rejectForm}
                    layout="vertical"
                    onFinish={handleReject}
                >
                    <Paragraph>
                        Vous allez rejeter la candidature de{' '}
                        <Text strong>
                            {application.firstName} {application.lastName}
                        </Text>.
                    </Paragraph>
                    <Form.Item
                        name="reason"
                        label="Raison du rejet"
                        rules={[{ required: true, message: 'Veuillez indiquer la raison du rejet' }]}
                    >
                        <TextArea
                            rows={4}
                            placeholder="Expliquez pourquoi cette candidature est rejetee..."
                        />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setRejectModalVisible(false)}>
                                Annuler
                            </Button>
                            <Button type="primary" danger htmlType="submit" loading={rejecting}>
                                Rejeter
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default DetailApplicationPage;
